import { TemplateTypes } from '../../../../../../../lib/models/TemplateTypes';
import { isLength } from '../../../../../../../lib/util/helpers';
import { getCurrentPageNextElementId, updateElementsAfterAnswering } from '../../../utils/end-user-flow-helpers';
import { getNextElementsIdsOnPage } from '../hooks/setNextFlow';

/**
 * Toggles one answer the way a click does: a second click unselects it, single choice keeps one answer,
 * multiple choice keeps only the answers of the clicked answer's exception group.
 * @returns {{ resultAnswers: string[], resultScoring: object[] }} answer ids for the element and answer objects for scoring
 */
export function getToggledAnswerSelection({ answers = [], multipleChoice, selectedAnswers = [], scoringAnswers = [], answer }) {
  const isUnselect = selectedAnswers.includes(answer.id);

  const sameGroupAnswerIds = new Set(answers.filter(item => item?.exception?.value === answer.exception?.value).map(item => item.id));
  const sameGroupSelectedAnswers = selectedAnswers.filter(answerId => sameGroupAnswerIds.has(answerId));
  const updatedSelectedAnswers = multipleChoice ? [...sameGroupSelectedAnswers, answer.id] : [answer.id];
  const updatedUnselectedAnswers = selectedAnswers.filter(answerId => answerId !== answer.id);

  const sameGroupScoringAnswers = multipleChoice ? scoringAnswers.filter(item => item?.exception?.value === answer.exception?.value) : scoringAnswers;
  const selectedAnswersScoring = multipleChoice ? [...sameGroupScoringAnswers, answer] : [answer];
  const unselectedAnswersScoring = sameGroupScoringAnswers.filter(item => item.id !== answer.id);

  return {
    resultAnswers: isUnselect ? updatedUnselectedAnswers : updatedSelectedAnswers,
    resultScoring: isUnselect ? unselectedAnswersScoring : selectedAnswersScoring,
  };
}

/**
 * What the current page shows after an answer: the visible elements up to the question plus the branch it opens,
 * and the product elements of the page that stay outside, so their products get unselected.
 */
export function getAnswerFlowOnPage({ questionFlow, pageElements = [], currentElementsIds = [], questionId }) {
  const nextElementId = getCurrentPageNextElementId(questionFlow?.goTo, pageElements);
  const questionIndex = currentElementsIds.indexOf(questionId);
  const { nextElementIds, flowOnCurrentPage } = getNextElementsIdsOnPage(questionFlow, pageElements);
  const filteredCurrentElementsIds = flowOnCurrentPage ? currentElementsIds.filter((id, index) => index <= questionIndex) : currentElementsIds;
  const updatedCurrentElementsIds = isLength(nextElementIds) ? [...filteredCurrentElementsIds, ...nextElementIds] : [...filteredCurrentElementsIds];

  return {
    nextElement: nextElementId ? pageElements.find(el => el.id === questionFlow?.goTo) : undefined,
    updatedCurrentElementsIds,
    nonFlowProductElements: pageElements.filter(el => el.templateType === TemplateTypes.PRODUCT && !updatedCurrentElementsIds.includes(el.id)),
  };
}

/**
 * Writes an answer selection into the pages and the scoring, and resets the products of the given product elements.
 * Shared by the click and the prefill, so both leave the same data behind.
 */
export function applyAnswerSelection({
  allPages = [],
  answersScoring = {},
  pageId,
  questionId,
  resultAnswers,
  resultScoring,
  nonFlowProductElements = [],
}) {
  return {
    allPages: allPages.map(page =>
      page.id === pageId
        ? { ...page, elements: updateElementsAfterAnswering(page.elements, questionId, resultAnswers, nonFlowProductElements) }
        : page
    ),
    answersScoring: { ...answersScoring, [questionId]: { selectedAnswers: resultScoring } },
    unselectedProductIds: nonFlowProductElements.flatMap(el => el?.content?.products ?? []).map(product => product?.id),
  };
}
